import { User } from '@features/settings/types/settings.types';
import api from '@shared/services/api';
import { UserProfile } from '@shared/types/profile.types';
import { useCallback, useRef } from 'react';

interface CachedProfile {
  profile: UserProfile | null;
  timestamp: number;
}

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

// Global cache and in-flight requests
const profileCache: Record<string, CachedProfile> = {};
const requestsInFlight: Record<string, Promise<UserProfile | null>> = {};

export const useActorProfiles = () => {
  const getActorProfile = useCallback(async (actorId: string): Promise<UserProfile | null> => {
    const now = Date.now();
    const cached = profileCache[actorId];

    // Return cached profile if it exists and hasn't expired
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.profile;
    }

    // If there's already a request in flight for this profile, return that promise
    if (requestsInFlight[actorId]) {
      return requestsInFlight[actorId];
    }

    // Create new request promise
    const request = (async () => {
      try {
        const response = await api.get<User>(`/users/profile/${actorId}`);
        const profile = response.data.profile;

        // Update cache
        profileCache[actorId] = {
          profile,
          timestamp: now,
        };

        return profile;
      } catch (error) {
        console.error('Error fetching actor profile:', error);
        return null;
      } finally {
        // Clean up the in-flight request
        delete requestsInFlight[actorId];
      }
    })();

    // Store the request promise
    requestsInFlight[actorId] = request;

    return request;
  }, []);

  return { getActorProfile };
};
